import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
    faArrowLeft,
    faFilePdf,
    faHandPointer,
    faMagnifyingGlass,
    faRotateRight,
    faVolumeHigh,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Layout from '../../components/global/Layout'
import {
    FILE_TYPE_LABEL,
    JENIS_BUKU_LABEL,
    addRecentBook,
    addRecentQuery,
    getFilterOptions,
    getJenjangLabel,
    loadMiniSearch,
    resolveBookSlug,
    retryMiniSearch,
    searchBooks,
    subscribeMiniSearch,
} from '../../utils/minisearch'
import styles from './Pencarian.module.scss'

const FILE_TYPE_ICON = {
    pdf: faFilePdf,
    audio: faVolumeHigh,
    interactive: faHandPointer,
}

const JENIS_FILTERS = [
    { key: 'semua', label: 'Semua' },
    { key: 'kurmer', label: 'Kurikulum Merdeka' },
    { key: 'k13', label: 'K13' },
    { key: 'nonteks', label: 'Non-Teks' },
]

const PAGE_SIZE = 12
const MIN_QUERY_LENGTH = 2
const PLACEHOLDER_COVER = '/assets/image/catalog/book-placeholder.jpg'

/**
 * Halaman hasil pencarian penuh (tujuan tombol "Lihat semua" di Spotlight).
 * State tersimpan di URL: /pencarian?q=matematika&jenis=kurmer&jenjang=SD&tipe=pdf
 */
const Pencarian = () => {
    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams()

    const q = searchParams.get('q') || ''
    const jenis = searchParams.get('jenis') || 'semua'
    const jenjang = searchParams.get('jenjang') || ''
    const tipe = searchParams.get('tipe') || ''

    const [input, setInput] = useState(q)
    const [status, setStatus] = useState('loading')
    const [errorMessage, setErrorMessage] = useState('')
    const [options, setOptions] = useState({ jenjang: [], tipe: [] })
    const [result, setResult] = useState({ items: [], total: 0 })
    const [visible, setVisible] = useState(PAGE_SIZE)
    const [version, setVersion] = useState(0)
    const [pendingId, setPendingId] = useState(null)
    const firstLoadRef = useRef(true)

    const applySnapshot = (snap) => {
        if (snap?.miniSearch) {
            setStatus('ready')
            setErrorMessage('')
            setOptions(getFilterOptions())
        } else {
            setStatus('error')
            setErrorMessage(snap?.error || 'Data pencarian tidak tersedia.')
        }
    }

    // Muat index + ikuti auto-sync data katalog
    useEffect(() => {
        let mounted = true
        loadMiniSearch().then((snap) => {
            if (mounted) applySnapshot(snap)
        })

        const unsubscribe = subscribeMiniSearch(() => {
            if (!mounted) return
            setVersion((prev) => prev + 1)
            setOptions(getFilterOptions())
        })

        return () => {
            mounted = false
            unsubscribe()
        }
    }, [])

    // Input mengikuti URL (mis. dari tombol back)
    useEffect(() => {
        setInput(q)
    }, [q])

    // Ketik -> perbarui URL (debounce)
    useEffect(() => {
        const trimmed = input.trim()
        if (trimmed === q.trim()) return

        const timer = setTimeout(() => {
            const next = new URLSearchParams(searchParams)

            if (trimmed.length >= 1) next.set('q', trimmed)
            else next.delete('q')

            setSearchParams(next, { replace: true })
        }, 300)

        return () => clearTimeout(timer)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [input])

    // Jalankan pencarian saat URL berubah
    useEffect(() => {
        if (status !== 'ready') return

        const trimmed = q.trim()
        if (trimmed.length < MIN_QUERY_LENGTH) {
            setResult({ items: [], total: 0 })
            return
        }

        setResult(searchBooks(trimmed, { limit: Number.MAX_SAFE_INTEGER, jenis, jenjang, tipe }))

        if (firstLoadRef.current) {
            addRecentQuery(trimmed)
            firstLoadRef.current = false
        }

        setVisible(PAGE_SIZE)
    }, [q, jenis, jenjang, tipe, status, version])

    const updateParam = (key, value) => {
        const next = new URLSearchParams(searchParams)

        if (value) next.set(key, value)
        else next.delete(key)

        setSearchParams(next, { replace: true })
    }

    const resetFilters = () => {
        const next = new URLSearchParams()
        if (q.trim()) next.set('q', q.trim())
        setSearchParams(next, { replace: true })
    }

    const openBook = async (item) => {
        if (pendingId) return

        setPendingId(item.id)
        addRecentBook(item)

        const slug = item.slug || (await resolveBookSlug(item.id))
        setPendingId(null)
        navigate(slug ? `/katalog/${slug}` : `/katalog/${item.id}`)
    }

    const filtered = jenis !== 'semua' || Boolean(jenjang) || Boolean(tipe)
    const shown = result.items.slice(0, visible)
    const hasQuery = q.trim().length >= MIN_QUERY_LENGTH

    const renderCard = (item) => {
        const icon = FILE_TYPE_ICON[item.tipe_file] || faFilePdf

        return (
            <div className="col-6 col-md-4 col-lg-3" key={item.id}>
                <button
                    type="button"
                    className={styles.card}
                    onClick={() => openBook(item)}
                    aria-busy={pendingId === item.id}
                >
                    <span className={styles.coverWrap}>
                        <img
                            src={item.image || PLACEHOLDER_COVER}
                            alt=""
                            aria-hidden="true"
                            loading="lazy"
                            className={styles.cover}
                            onError={(event) => {
                                event.currentTarget.onerror = null
                                event.currentTarget.src = PLACEHOLDER_COVER
                            }}
                        />
                    </span>

                    <span className={styles.badges}>
                        <span className={styles.badge}>
                            <FontAwesomeIcon icon={icon} className="me-1" />
                            {FILE_TYPE_LABEL[item.tipe_file] || item.tipe_file}
                        </span>
                        {item.jenjang && (
                            <span className={styles.badge}>{getJenjangLabel(item.jenjang)}</span>
                        )}
                    </span>

                    <span className={styles.title}>{item.judul}</span>

                    <span className={styles.meta}>
                        {[item.mapel, item.kelas ? `Kelas ${item.kelas}` : '', JENIS_BUKU_LABEL[item.jenis_buku] || item.jenis_buku]
                            .filter(Boolean)
                            .join(' · ')}
                    </span>
                </button>
            </div>
        )
    }

    return (
        <Layout>
            <section className={`container ${styles.page}`}>
                <button type="button" className={styles.back} onClick={() => navigate(-1)}>
                    <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                    Kembali
                </button>

                <h1 className={styles.heading}>
                    {hasQuery ? (
                        <>
                            Hasil pencarian <span className={styles.keyword}>“{q}”</span>
                        </>
                    ) : (
                        'Pencarian buku'
                    )}
                </h1>

                <div className={styles.searchBox}>
                    <FontAwesomeIcon icon={faMagnifyingGlass} className={styles.searchIcon} />
                    <input
                        type="search"
                        value={input}
                        onChange={(event) => setInput(event.target.value)}
                        className={styles.searchInput}
                        placeholder="Cari judul, mapel, kelas, atau jenjang…"
                        aria-label="Cari buku"
                    />
                </div>

                <div className={styles.filters}>
                    <div className={styles.chips}>
                        {JENIS_FILTERS.map((filter) => (
                            <button
                                key={filter.key}
                                type="button"
                                onClick={() => updateParam('jenis', filter.key === 'semua' ? '' : filter.key)}
                                className={`${styles.chip} ${jenis === filter.key ? styles.chipActive : ''}`}
                                aria-pressed={jenis === filter.key}
                            >
                                {filter.label}
                            </button>
                        ))}
                    </div>

                    <div className={styles.selects}>
                        <select
                            className={styles.select}
                            value={jenjang}
                            onChange={(event) => updateParam('jenjang', event.target.value)}
                            aria-label="Filter jenjang"
                        >
                            <option value="">Semua jenjang</option>
                            {options.jenjang.map((item) => (
                                <option key={item} value={item}>
                                    {getJenjangLabel(item)}
                                </option>
                            ))}
                        </select>

                        <select
                            className={styles.select}
                            value={tipe}
                            onChange={(event) => updateParam('tipe', event.target.value)}
                            aria-label="Filter tipe buku"
                        >
                            <option value="">Semua tipe</option>
                            {options.tipe.map((item) => (
                                <option key={item} value={item}>
                                    {FILE_TYPE_LABEL[item] || item}
                                </option>
                            ))}
                        </select>

                        {filtered && (
                            <button type="button" className={styles.reset} onClick={resetFilters}>
                                Hapus filter
                            </button>
                        )}
                    </div>
                </div>

                {status === 'loading' && (
                    <div className={styles.state}>
                        <FontAwesomeIcon icon={faRotateRight} spin className="me-2" />
                        Menyiapkan data pencarian…
                    </div>
                )}

                {status === 'error' && (
                    <div className={styles.state}>
                        {errorMessage}
                        <button
                            type="button"
                            className={styles.retry}
                            onClick={() => {
                                setStatus('loading')
                                retryMiniSearch().then(applySnapshot)
                            }}
                        >
                            Coba lagi
                        </button>
                    </div>
                )}

                {status === 'ready' && !hasQuery && (
                    <div className={styles.state}>
                        Ketik minimal {MIN_QUERY_LENGTH} huruf untuk mulai mencari buku.
                    </div>
                )}

                {status === 'ready' && hasQuery && result.total === 0 && (
                    <div className={styles.state}>
                        Tidak ada buku yang cocok dengan “{q}”.
                        {filtered && (
                            <button type="button" className={styles.retry} onClick={resetFilters}>
                                Hapus filter
                            </button>
                        )}
                    </div>
                )}

                {status === 'ready' && result.total > 0 && (
                    <>
                        <p className={styles.count}>
                            Menampilkan {shown.length} dari {result.total} buku
                        </p>

                        <div className="row g-4">{shown.map(renderCard)}</div>

                        {visible < result.total && (
                            <div className="text-center mt-4">
                                <button
                                    type="button"
                                    className={styles.loadMore}
                                    onClick={() => setVisible((prev) => prev + PAGE_SIZE)}
                                >
                                    Muat lebih banyak
                                </button>
                            </div>
                        )}
                    </>
                )}
            </section>
        </Layout>
    )
}

export default Pencarian
